import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { apiClient } from "@/services/core/apiClient";
import POSSelection from "@/components/business/pos/POSSelection";
import { usePOSContext } from "@/layouts/DeliveryLayout";

/**
 * Interfaz para pedidos del KDS de Cajero (simplificado)
 */
interface CajeroOrder {
  id: string;
  orderNumber: string;
  items: Array<{
    id: string;
    productName: string;
    quantity: number;
    type: 'snack' | 'bebida' | 'dulce';
  }>;
  customerName: string;
  status: 'EN_PREPARACION' | 'LISTO' | 'ENTREGADO';
  createdAt: string;
  readyAt?: string;
}

/**
 * KDS Simplificado para Cajeros
 * 
 * Para productos que no requieren preparación compleja:
 * - Snacks
 * - Bebidas 
 * - Dulces
 * - Productos empaquetados
 */
const CajeroKDSPage: React.FC = () => {
  const [orders, setOrders] = useState<CajeroOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const [orderMetadata, setOrderMetadata] = useState<any>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Usar el contexto de POS del DeliveryLayout
  const { posList, isLoadingPOS, selectedPOS, setSelectedPOS } = usePOSContext();

  /**
   * Carga los pedidos pendientes para cajero según el POS seleccionado
   */
  const loadOrders = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Si no hay POS seleccionado, no cargar pedidos
      if (!selectedPOS) {
        setOrders([]);
        return;
      }

      // Llamar al endpoint real con el posId
      const response = await apiClient.get(`/api/v1/orders/today/in-preparation/${selectedPOS.id}`);

      // Debug: Ver qué está devolviendo el backend
      console.log('Response from backend:', response);
      console.log('Response data:', response.data);

      // Extraer el array de órdenes de la estructura específica del backend
      // El backend devuelve: { success: true, message: "", data: { orders: [], metadata: {} } }
      let backendOrders = [];

      if (response.data && response.data.data && Array.isArray(response.data.data.orders)) {
        backendOrders = response.data.data.orders;
      } else {
        // Fallback para otras estructuras posibles
        backendOrders = response.data.data || response.data || [];

        // Validar que sea un array
        if (!Array.isArray(backendOrders)) {
          console.error('Backend response is not an array:', backendOrders);
          console.error('Type of response:', typeof backendOrders);

          // Si es un objeto con una propiedad que contiene el array, intentar extraerlo
          if (typeof backendOrders === 'object' && backendOrders !== null) {
            // Buscar posibles propiedades que contengan el array
            const possibleArrayKeys = ['orders', 'data', 'items', 'list', 'results'];
            for (const key of possibleArrayKeys) {
              if (Array.isArray(backendOrders[key])) {
                console.log(`Found array in property: ${key}`);
                backendOrders = backendOrders[key];
                break;
              }
            }
          }
          
          // Si aún no es un array, usar array vacío
          if (!Array.isArray(backendOrders)) {
            console.warn('Could not find array in response, using empty array');
            backendOrders = [];
          }
        }
      }
      
      // console.log('Final backendOrders array:', backendOrders);
      // console.log('Number of orders found:', backendOrders.length);
      
      const mappedOrders: CajeroOrder[] = backendOrders.map((order: any) => ({
        id: order.id,
        orderNumber: order.orderNumber || order.numero || `#${order.id}`,
        items: order.items?.map((item: any) => ({
          id: item.id,
          productName: item.productName || item.product_name || item.nombre || item.producto,
          quantity: item.quantity ?? item.cantidad,
          type: item.type || item.tipo || item.product_type || 'snack'
        })) || [],
        customerName: order.customerName || order.cliente_nombre_anonimo || order.cliente || 'Cliente',
        status: order.status || order.estado || 'EN_PREPARACION',
        createdAt: order.createdAt || order.creadoEn || order.fechaCreacion || new Date().toISOString(),
        readyAt: order.readyAt || order.fechaListo || undefined,
      }));

      setOrders(mappedOrders);
      
      // Guardar metadatos si están disponibles
      if (response.data && response.data.data && response.data.data.metadata) {
        // setOrderMetadata(response.data.data.metadata);
        console.log('Order metadata:', response.data.data.metadata);
      }
    } catch (err: any) {
      console.error("Error cargando pedidos de cajero:", err);
      setError(err.message || "Error al cargar los pedidos");
      toast({
        title: "Error",
        description: "No se pudieron cargar los pedidos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('orders', orders);
  }, [orders]);

  /**
   * Obtiene el ícono según el tipo de producto
   */
  const getProductIcon = (type: string) => {
    switch (type) {
      case 'bebida': return '🥤';
      case 'snack': return '🍿';
      case 'dulce': return '🍫';
      default: return '📦';
    }
  };

  /**
   * Formatea el tiempo transcurrido
   */
  const formatTimeAgo = (dateString: string) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return "Recién llegado";
    if (minutes === 1) return "Hace 1 minuto";
    return `Hace ${minutes} minutos`;
  };

  // Cargar pedidos al montar y cuando cambie el POS seleccionado
  useEffect(() => {
    loadOrders();
  }, [selectedPOS]); // Dependencia del POS seleccionado

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, [selectedPOS]); // También dependiente del POS para el auto-refresh

  // Verificar autenticación
  if (!user) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Acceso Requerido</h3>
            <p className="text-muted-foreground">
              Debes iniciar sesión para acceder al KDS de Cajero
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingOrders = orders.filter(order => order.status === 'EN_PREPARACION');

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            KDS Cajero {selectedPOS ? `- ${selectedPOS.nombre}` : ''}
          </h1>
          <p className="text-muted-foreground">
            {selectedPOS 
              ? `Sistema de pedidos para ${selectedPOS.nombre}` 
              : 'Sistema de pedidos para snacks y bebidas'}
          </p>
        </div>
        
        <Button
          onClick={loadOrders}
          variant="outline"
          size="sm"
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Selección de POS */}
      <POSSelection 
        className="mb-4" 
        onPOSChange={setSelectedPOS}
        externalPOSList={posList}
        externalSelectedPOS={selectedPOS}
        externalIsLoading={isLoadingPOS}
      />

      {/* Estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pendientes
            </CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {pendingOrders.length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Hoy
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {orders.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mensaje cuando no hay POS seleccionado */}
      {!selectedPOS && (
        <div className="flex items-center justify-center py-12">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Seleccione un Punto de Venta</h3>
              <p className="text-muted-foreground">
                Elija un POS para ver los pedidos correspondientes
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading */}
      {selectedPOS && isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Cargando pedidos...</p>
          </div>
        </div>
      )}

      {/* Error */}
      {selectedPOS && error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders Grid */}
      {selectedPOS && !isLoading && !error && (
        <div>
          {/* Pedidos Pendientes */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              Pedidos Pendientes ({pendingOrders.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingOrders.map((order) => (
                <Card key={order.id} className="border-orange-200 bg-orange-50/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">#{order.orderNumber}</h3>
                        <p className="text-sm text-muted-foreground">
                          {order.customerName}
                        </p>
                      </div>
                      <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                        {formatTimeAgo(order.createdAt)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2">
                      {order.items.map((item) => (
                        <div key={item.id} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span>{getProductIcon(item.type)}</span>
                            {item.productName}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            x{item.quantity}
                          </Badge>
                        </div>
                      ))}
                    </div>
                    <div className="text-center text-sm text-muted-foreground">
                      Los pedidos se completarán automáticamente al escanear el QR
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {pendingOrders.length === 0 && (
                <div className="col-span-full">
                  <Card className="border-dashed">
                    <CardContent className="pt-6 text-center text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No hay pedidos pendientes</p>
                      <p className="text-sm mt-2">Los nuevos pedidos aparecerán aquí automáticamente</p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CajeroKDSPage;
